import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { 
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';
import { apiService } from '../services/api';
import { SiteAnalysisResult } from '../types';
import RegionSelector from '../components/RegionSelector';

// Цветовая схема из онбординга
const FigmaColors = {
  background: '#FFFFFF',
  darkBg: '#d0e1f9',
  inputBg: 'rgb(248, 242, 255)', // Фон полей форм
  inputPlaceholder: '#000000',
  primaryButton: '#4d648d',
  primaryButtonGradient: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
  chipBorder: '#4d648d',
  progressBg: '#d0e1f9',
  progressFill: '#4d648d',
  spacerDark: '#d0e1f9',
  headingColor: '#000000', // Цвет заголовков
};

// Данные для анализа сайта
interface SiteData {
  url: string;
  analysis?: SiteAnalysisResult;
}

// Данные кампании как в онбординге
interface CampaignFormData {
  goal: 'sales' | 'leads' | 'awareness' | null;
  budget: string;
  geo: string | null; // Now supports any region slug
}

const CreateCampaignPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useTelegramWebApp();
  
  // Состояние для управления шагами
  const [currentStep, setCurrentStep] = useState<'site-analysis' | 'campaign-params'>('site-analysis');
  
  // Состояние для анализа сайта
  const [siteData, setSiteData] = useState<SiteData>({
    url: ''
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState('');

  // Состояние для данных кампании
  const [campaignData, setCampaignData] = useState<CampaignFormData>({
    goal: null,
    budget: '',
    geo: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleGoBack = () => {
    if (currentStep === 'campaign-params') {
      setCurrentStep('site-analysis');
    } else {
      navigate('/campaigns');
    }
  };

  const handleEditSiteAnalysis = () => {
    setCurrentStep('site-analysis');
  };

  // Обработчик анализа сайта
  const handleAnalyzeSite = async () => {
    if (!siteData.url.trim()) {
      setAnalysisError('Введите URL сайта');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError('');

    try {
      // Нормализуем URL - добавляем протокол если его нет
      const trimmedUrl = siteData.url.trim();
      const hasProtocol = /^https?:\/\//i.test(trimmedUrl);
      const normalizedUrl = hasProtocol ? trimmedUrl : `https://${trimmedUrl}`;

      // Проверяем валидность URL
      try {
        new URL(normalizedUrl);
      } catch {
        setAnalysisError('Введите корректную ссылку на сайт');
        setIsAnalyzing(false);
        return;
      }

      // Обновляем URL в состоянии
      setSiteData(prev => ({ ...prev, url: normalizedUrl }));

      const result = await apiService.analyzeOnboardingSite(normalizedUrl, user?.id);
      setSiteData(prev => ({
        ...prev,
        analysis: result.analysis
      }));
      
      // Переходим к следующему шагу после успешного анализа
      setCurrentStep('campaign-params');
    } catch (error: any) {
      setAnalysisError(error?.message || 'Не удалось проанализировать сайт');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Обработчик изменения данных кампании
  const handleCampaignInputChange = (field: keyof CampaignFormData, value: string | null) => {
    setCampaignData(prev => ({ ...prev, [field]: value }));
    // Очищаем ошибку для этого поля
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Валидация формы кампании
  const validateCampaignForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!campaignData.goal) {
      errors.goal = 'Выберите цель кампании';
    }
    
    if (!campaignData.budget?.trim()) {
      errors.budget = 'Бюджет обязателен';
    } else {
      const budgetNum = parseFloat(campaignData.budget);
      if (isNaN(budgetNum) || budgetNum <= 0) {
        errors.budget = 'Введите корректный бюджет';
      }
    }
    
    if (!campaignData.geo) {
      errors.geo = 'Выберите географию';
    }

    if (!siteData.analysis) {
      errors.site = 'Необходимо проанализировать сайт';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Обработчик создания кампании
  const handleSubmit = async () => {
    if (!validateCampaignForm()) return;

    if (!user?.id) {
      setSubmitError('Необходимо авторизоваться через Telegram');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Генерируем название кампании автоматически как в онбординге
      const siteName = new URL(siteData.url).hostname.replace('www.', '');
      const goalText = campaignData.goal === 'sales' ? 'Продажи' :
                      campaignData.goal === 'leads' ? 'Лиды' :
                      campaignData.goal === 'awareness' ? 'Узнаваемость' : 'Продажи';
      const generatedName = `Кампания ${siteName} ${goalText}`;

      // Подготавливаем данные для API как в онбординге
      const campaignPayload = {
        name: generatedName,
        goal: campaignData.goal!,
        budget: parseFloat(campaignData.budget),
        geo: campaignData.geo!,
        business_profile: siteData.analysis!,
        site_url: siteData.url,
        telegram_user_id: user.id
      };

      console.log('🚀 Отправляем кампанию:', campaignPayload);

      const result = await apiService.createCampaignFromOnboarding(campaignPayload);
      
      console.log('✅ Кампания создана:', result);
      
      // Переходим на страницу созданной кампании
      if (result.campaign_id) {
        navigate(`/campaigns/${result.campaign_id}`);
      } else {
        // Fallback к списку кампаний, если ID не получен
        navigate('/campaigns');
      }

    } catch (error: any) {
      console.error('❌ Ошибка создания кампании:', error);
      setSubmitError(error?.message || 'Не удалось создать кампанию');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Маппинги как в онбординге
  const goalMapping = {
    'Продажи': 'sales' as const,
    'Лиды': 'leads' as const,
    'Узнаваемость': 'awareness' as const,
  };


  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: FigmaColors.background, 
      color: FigmaColors.headingColor 
    }}>
      <Container maxWidth="sm" sx={{ py: 2 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3,
        }}>
          <Button
            onClick={handleGoBack}
            sx={{ 
              minWidth: 48, 
              width: 48, 
              height: 48, 
              color: FigmaColors.headingColor,
              p: 0,
              mr: 2
            }}
          >
            <ArrowBack />
          </Button>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              color: FigmaColors.headingColor,
              flexGrow: 1
            }}
          >
            {currentStep === 'site-analysis' ? 'Анализ сайта' : 'Параметры кампании'}
          </Typography>
        </Box>

        {/* Секция анализа сайта */}
        {currentStep === 'site-analysis' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: FigmaColors.headingColor, mb: 2 }}>
              Анализ сайта
            </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                bgcolor: FigmaColors.inputBg,
                borderRadius: '36px',
                p: 2,
                border: analysisError ? '1px solid #f44336' : 'none',
              }}
            >
              <input
                type="url"
                placeholder="Введите URL вашего сайта"
                value={siteData.url}
                onChange={(e) => setSiteData(prev => ({ ...prev, url: e.target.value }))}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: FigmaColors.inputPlaceholder,
                  fontSize: '16px',
                  fontFamily: 'inherit',
                }}
              />
            </Box>
            {analysisError && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                {analysisError}
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handleAnalyzeSite}
            disabled={isAnalyzing || !siteData.url.trim()}
            sx={{
              background: FigmaColors.primaryButtonGradient,
              color: '#ffffff !important',
              boxShadow: 'none',
              borderRadius: 3,
              height: 48,
              fontWeight: 700,
              mb: 2,
              '&:hover': { 
                background: FigmaColors.primaryButtonGradient,
                opacity: 0.9,
                boxShadow: 'none',
                color: '#ffffff !important'
              },
              '&:disabled': { 
                bgcolor: FigmaColors.inputBg, 
                color: '#000000' 
              },
            }}
          >
            {isAnalyzing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: FigmaColors.darkBg }} />
                Анализируем...
              </Box>
            ) : (
              'Анализировать сайт'
            )}
          </Button>

          {/* Результат анализа */}
          {siteData.analysis && (
            <Card elevation={0} sx={{ bgcolor: FigmaColors.inputBg, borderRadius: '36px' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: FigmaColors.headingColor, mb: 1 }}>
                  Портрет бизнеса
                </Typography>
                <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 1 }}>
                  <strong>Описание:</strong> {siteData.analysis.summary}
                </Typography>
                <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 1 }}>
                  <strong>Источник анализа:</strong> {siteData.analysis.source === 'ai' ? 'ИИ-анализ' : 'Эвристический анализ'}
                </Typography>
                <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder }}>
                  <strong>Язык:</strong> {siteData.analysis.lang}
                </Typography>
              </CardContent>
            </Card>
          )}
          </Box>
        )}

        {/* Секция параметров кампании */}
        {currentStep === 'campaign-params' && (
          <Box sx={{ mb: 3 }}>
            {/* Краткая информация о проанализированном сайте */}
            {siteData.analysis && (
              <Card elevation={0} sx={{ bgcolor: FigmaColors.inputBg, borderRadius: '36px', mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: FigmaColors.headingColor }}>
                      Проанализированный сайт
                    </Typography>
                    <Button 
                      size="small" 
                      onClick={handleEditSiteAnalysis}
                      sx={{ 
                        color: FigmaColors.primaryButton,
                        textTransform: 'none',
                        fontSize: '0.875rem'
                      }}
                    >
                      Изменить
                    </Button>
                  </Box>
                  <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 1 }}>
                    <strong>URL:</strong> {siteData.url}
                  </Typography>
                  <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder }}>
                    <strong>Описание:</strong> {siteData.analysis.summary}
                  </Typography>
                </CardContent>
              </Card>
            )}

            <Typography variant="h6" sx={{ fontWeight: 700, color: FigmaColors.headingColor, mb: 2 }}>
              Параметры кампании
            </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {formErrors.site && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {formErrors.site}
            </Alert>
          )}


          {/* Цель кампании */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: FigmaColors.headingColor }}>
              Цель кампании
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(goalMapping).map(([label, value]) => (
                <Chip
                  key={label}
                  label={label}
                  clickable
                  variant={campaignData.goal === value ? "filled" : "outlined"}
                  onClick={() => handleCampaignInputChange('goal', value)}
                  sx={{
                    borderColor: campaignData.goal === value ? '#000000' : '#ececfc',
                    border: campaignData.goal === value ? '1px solid #000000' : '1px solid #ececfc',
                    color: campaignData.goal === value ? '#ffffff' : '#514b81',
                    bgcolor: campaignData.goal === value ? '#000000' : '#ffffff',
                    '&:hover': {
                      bgcolor: campaignData.goal === value ? '#000000' : '#ffffff',
                      opacity: 0.8,
                    },
                  }}
                />
              ))}
            </Box>
            {formErrors.goal && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                {formErrors.goal}
              </Typography>
            )}
          </Box>

          {/* Бюджет */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: FigmaColors.headingColor }}>
              Бюджет
            </Typography>
            <Box
              sx={{
                bgcolor: FigmaColors.inputBg,
                borderRadius: '36px',
                p: 2,
                border: formErrors.budget ? '1px solid #f44336' : 'none',
              }}
            >
              <input
                type="number"
                placeholder="Введите бюджет рекламной кампании"
                value={campaignData.budget}
                onChange={(e) => handleCampaignInputChange('budget', e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: FigmaColors.inputPlaceholder,
                  fontSize: '16px',
                  fontFamily: 'inherit',
                }}
              />
            </Box>
            {formErrors.budget && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                {formErrors.budget}
              </Typography>
            )}
          </Box>

          {/* География */}
          <RegionSelector
            selectedRegion={campaignData.geo}
            onRegionChange={(regionSlug) => handleCampaignInputChange('geo', regionSlug)}
            error={formErrors.geo}
          />

          {/* Кнопка создания кампании */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={isSubmitting || !siteData.analysis}
            sx={{
              background: FigmaColors.primaryButtonGradient,
              color: '#ffffff !important',
              boxShadow: 'none',
              borderRadius: 3,
              height: 48,
              fontWeight: 700,
              '&:hover': { 
                background: FigmaColors.primaryButtonGradient,
                opacity: 0.9,
                boxShadow: 'none',
                color: '#ffffff !important'
              },
              '&:disabled': { 
                bgcolor: FigmaColors.inputBg, 
                color: '#000000' 
              },
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: FigmaColors.darkBg }} />
                Создаем кампанию...
              </Box>
            ) : (
              'Создать кампанию'
            )}
          </Button>
          </Box>
        )}

    </Container>
    </Box>
  );
};

export default CreateCampaignPage;